import React, { useEffect } from 'react';
import { TravelAbilityWordmark } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { assessmentData } from '../data/assessmentData';
import { 
  Target, TrendingUp, Trophy, HelpCircle,
  CheckCircle2, Clock, Calendar
} from 'lucide-react';

const profileIcons = {
  'awakening': Target,
  'momentum': TrendingUp,
  'advanced': Trophy,
  'uncertain': HelpCircle
};

export const AssessmentResults = ({ lead, results, onRestart }) => {
  const { profile } = results;
  const ProfileIcon = profileIcons[profile.id] || Target;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#111111]">
      <div className="fixed inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1749976609807-dc05ec2dbbdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxpbmNsdXNpdmUlMjB0b3VyaXNtJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHRyYXZlbGluZ3xlbnwwfHx8fDE3NzU5MjE2NzB8MA&ixlib=rb-4.1.0&q=85)' }} />
      <div className="fixed inset-0 bg-gradient-to-b from-[#111111]/80 via-[#111111]/90 to-[#111111]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex justify-center mb-8"><TravelAbilityWordmark /></div>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mb-2" data-testid="results-title">
            RESULTS & RECOMMENDATIONS
          </h1>
          <p className="text-lg text-[#9CA3AF]">Your Destination Type → Your Next Step</p>
        </div>

        {/* Profile Card */}
        <Card className="mb-8 overflow-hidden" style={{ borderColor: profile.color, borderWidth: '2px' }}>
          <div className="h-2" style={{ backgroundColor: profile.color }} />
          <CardContent className="p-8">
            <div className="flex items-start gap-5 mb-6">
              <div className="p-4 rounded-2xl" style={{ backgroundColor: `${profile.color}20` }}>
                <ProfileIcon className="w-10 h-10" style={{ color: profile.color }} />
              </div>
              <div>
                <p className="text-sm text-[#9CA3AF] mb-1">Your Profile</p>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: profile.color }} data-testid="profile-title">
                  {profile.title}
                </h2>
                <p className="text-sm text-[#6B7280] mt-1 italic">{profile.pattern}</p>
              </div>
            </div>
            <p className="text-[#9CA3AF] text-lg leading-relaxed mb-6">{profile.summary}</p>

            {/* Current State */}
            <div className="p-5 bg-[#222222] rounded-xl mb-6">
              <h4 className="font-bold text-[#F3F4F6] mb-3">Your Current State:</h4>
              <ul className="space-y-2">
                {profile.currentState.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ backgroundColor: profile.color }} />
                    <span className="text-[#9CA3AF]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Why This Fits */}
            <div className="p-5 bg-[#222222] rounded-xl mb-6">
              <h4 className="font-bold text-[#FBBF24] mb-2">What This Means:</h4>
              <p className="text-[#9CA3AF] leading-relaxed">{profile.whyFits}</p>
            </div>

            {/* Timeline */}
            <div className="flex items-center gap-3 p-4 bg-[#111111] rounded-xl">
              <Clock className="w-5 h-5 text-[#FBBF24]" />
              <span className="text-[#F3F4F6] font-semibold">Timeline:</span>
              <span className="text-[#9CA3AF]">{profile.timeline}</span>
            </div>
          </CardContent>
        </Card>

        {/* Single CTA */}
        <div className="mb-8 text-center">
          {profile.ctas.map((cta, i) => (
            cta.primary && (
              <a key={i} href={cta.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] hover:bg-[#F59E0B] text-[#111111] font-bold text-lg px-8 py-4 rounded-xl transition-colors"
                data-testid="advisory-call-btn">
                <Calendar className="w-5 h-5" />
                {cta.text}
              </a>
            )
          ))}
        </div>

        {/* What Happens Next */}
        <Card className="mb-8 bg-[#1A1A1A] border-[#333333]">
          <CardHeader>
            <CardTitle className="text-[#F3F4F6] text-center">What Happens Next</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {assessmentData.whatHappensNext.map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-4 bg-[#222222] rounded-xl">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FBBF24]/20 text-[#FBBF24] font-bold text-sm">{item.step}</span>
                  <div>
                    <p className="text-[#F3F4F6]">{item.text}</p>
                    <p className="text-sm text-[#6B7280]">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-[#FBBF24] font-semibold mt-6">No pressure. Just clarity.</p>
          </CardContent>
        </Card>

        {/* Credibility */}
        <Card className="mb-8 bg-[#1A1A1A] border-[#333333]">
          <CardHeader>
            <CardTitle className="text-[#F3F4F6] text-center">Why TravelAbility?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assessmentData.credibility.stats.map((stat, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-[#222222] rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9CA3AF]">{stat}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xl font-bold text-[#FBBF24] mt-6">{assessmentData.credibility.closing}</p>
          </CardContent>
        </Card>

        {/* Bottom */}
        <div className="text-center">
          <Button onClick={onRestart} variant="outline"
            className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]"
            data-testid="restart-btn">
            Take Assessment Again
          </Button>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-[#333333] text-center">
          <p className="text-[#6B7280] text-sm">TravelAbility - The Future of Inclusive Travel</p>
          <p className="text-[#6B7280] text-sm mt-2">
            Questions? Email{' '}
            <a href={`mailto:${assessmentData.contact.email}`} className="text-[#FBBF24] hover:underline">{assessmentData.contact.email}</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AssessmentResults;
